import { computed } from 'vue'

import type {
  GetLegacyServicePickersResponse,
  PickerService,
  PickersResponseServiceItem,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'

import type {
  MobilePlanPicker,
  MobilePlanPickerGroup,
  TradeInPicker,
  TradeInPickerGroup,
} from '~/api/product-page/types'
import { useFetchMobilePlanPickers } from '~/scopes/product-core/composables/useFetch/useFetchMobilePlanPickers/useFetchMobilePlanPickers'
import { useFetchTradeInPickers } from '~/scopes/product-core/composables/useFetch/useFetchTradeInPickers/useFetchTradeInPickers'

// Adapts PPI-7770's precise per-endpoint item shape back to the legacy, looser
// `PickersResponseServiceItem` the pre-existing PDP funnel tree (`CustomizationFunnel`,
// `createCustomizationFunnelData`, `hasItemExtraData`, ...) is built around — that tree
// is unrelated to this ticket and out of scope to retype. Tracked in PPI-7874: retype
// that tree against `MobilePlanPicker`/`TradeInPicker` directly and delete this adapter
// along with the legacy `PickerService`/`PickersResponseServiceItem` shapes.
function toLegacyItem(
  item: MobilePlanPicker | TradeInPicker,
): PickersResponseServiceItem {
  // `mobilePlanOffer` (on MobilePlanPicker only) is carried through at runtime
  // by the spread. The product-page service MobilePlanOffer omits
  // `documents`/`type` and nulls `baseOffer`/`portalUrl` (PPI-7770), so it does
  // not structurally match the partnerships-typed legacy field — but the legacy
  // funnel tree never reads those, so bridging through the legacy type is safe.
  // Tracked in PPI-7874: retype that tree against MobilePlanPicker/TradeInPicker.
  return {
    ...item,
    slug: item.slug ?? undefined,
    defaultForMarket: null,
    bestDeal: false,
    price: 'price' in item ? (item.price ?? undefined) : undefined,
    // The product-page service MobilePlanOffer omits `documents`/`type` and
    // nulls `baseOffer`/`portalUrl` (PPI-7770), so it doesn't structurally
    // match the partnerships-typed legacy field. The legacy funnel tree never
    // reads those, so bridging through the legacy type is safe here.
    // Tracked in PPI-7874: retype that tree and drop this cast.
    mobilePlanOffer: ('mobilePlanOffer' in item
      ? item.mobilePlanOffer
      : undefined) as PickersResponseServiceItem['mobilePlanOffer'],
  }
}

function toLegacyGroup(
  group: MobilePlanPickerGroup | TradeInPickerGroup,
): PickerService {
  return {
    id: group.id,
    label: group.label,
    sectionTitle: group.sectionTitle,
    trackingId: group.trackingId ?? undefined,
    guidanceCard: group.guidanceCard ?? undefined,
    sideContent: 'sideContent' in group ? group.sideContent : null,
    items: group.items.map(toLegacyItem),
  }
}

/**
 * Combines the mobile-plan pickers (server-side, PPI-7770) and trade-in pickers
 * (client-side) into the single `pickerGroups` shape the PDP UI expects, so consumers
 * (`ContainerCustomizationFunnel`, `ContainerStickyBarBottom`, `useFetchProductTracking`)
 * don't need to know the underlying data now comes from two separate endpoints.
 *
 * Because mobile-plan data resolves during SSR (see `useFetchMobilePlanPickers`), `data`
 * is populated from first render with at least the mobile-plan groups, with trade-in
 * appended once the client-only fetch lands.
 */
export function useFetchServicePickers() {
  const {
    data: mobilePlanPickersResponse,
    pending: isMobilePlanPending,
    error: mobilePlanError,
  } = useFetchMobilePlanPickers()
  const {
    data: tradeInPickersResponse,
    pending: isTradeInPending,
    error: tradeInError,
  } = useFetchTradeInPickers()

  const data = computed<GetLegacyServicePickersResponse | undefined>(() => {
    if (!mobilePlanPickersResponse.value && !tradeInPickersResponse.value) {
      return undefined
    }

    return {
      pickerGroups: [
        ...(mobilePlanPickersResponse.value?.pickerGroups.map(toLegacyGroup) ??
          []),
        ...(tradeInPickersResponse.value?.pickerGroups.map(toLegacyGroup) ??
          []),
      ],
    }
  })

  const pending = computed(
    () => isMobilePlanPending.value || isTradeInPending.value,
  )

  const error = computed(() => mobilePlanError.value || tradeInError.value)

  return { data, pending, error }
}
